<template>
  <div
    class="accordion-block"
    :class="{ [$class('open')]: isOpen, 'accordion-block--no-spacing': !spacing }"
  >
    <div
      v-for="(item, index) of accordionItems"
      :key="`accordion-block__item-${index}`"
      class="accordion-block__item"
      :class="{ [$class('active')]: (item.link === activePanel) }"
    >
      <button
        v-if="item.link === 'size-guide'"
        type="button"
        class="accordion-block__toggle text-mobile-regular-mobile"
        @click="(event) => handleOverlayClick(event, item.link)"
      >
        <span v-text="item.label" />

        <icon-arrow-down
          v-if="icon === 'chevron' && getActiveOverlay !== 'sizeGuide'"
        />

        <icon-arrow-up
          v-else-if="icon === 'chevron' && getActiveOverlay === 'sizeGuide'"
        />

        <icon-arrow-right
          v-else-if="icon === 'plus'"
        />
      </button>

      <button
        v-if="item.link !== 'size-guide'"
        type="button"
        class="accordion-block__toggle text-mobile-regular-mobile"
        :class="{ [$class('active')]: (item.link === activePanel) }"
        :aria-expanded="(item.link === activePanel)"
        @click="(event) => handlePanelClick(event, item.link)"
      >
        <span v-text="item.label" />

        <icon-arrow-down
          v-if="icon === 'chevron' && (item.link !== activePanel)"
        />

        <icon-arrow-up
          v-else-if="icon === 'chevron' && (item.link === activePanel)"
        />

        <icon-plus
          v-else-if="icon === 'plus' && (item.link !== activePanel)"
        />

        <icon-minus
          v-else-if="icon === 'plus' && (item.link === activePanel)"
        />
      </button>

      <div
        v-if="item.link !== 'size-guide'"
        v-show="item.link === activePanel"
        class="
          text-mobile-small-mobile
          text-desktop-small-desktop
          accordion-block__content
        "
      >
        <component
          :is="subMenuComponent(item.link)"
          :content="subMenuContent"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Accordion block (accordion-block)
 * -----------------------------------------------------------------------------
 * Expandable content sections, typically referred to as an accordion.
 *
 * @param {Array} accordionItems - Array of accordion item objects.
 * @param {Object} subMenuContent - Object with content keys for the sub-menu.
 * @param {Object} activeItem - Active accordion item.
 * @param {Boolean} spacing - Wheither the block should have top spacing or not
 * @param {String} toggleClass - Class name on toggle buttons
 * @param {String} icon - Type of icons. Accepted values is 'chevron' or 'plus'.
 * @param {String} isMobile - Boolean for whether this is the mobile display
 */
import IconArrowDown from '~icons/direction/arrow/down.svg'
import IconArrowRight from '~icons/direction/arrow/right.svg'
import IconArrowUp from '~icons/direction/arrow/up.svg'
import IconPlus from '~icons/misc/plus.svg'
import IconMinus from '~icons/misc/minus.svg'
import SubMenuGarderob from '~async/main-product/sub-menu/sub-menu-garderob'
import SubMenuComingSoon from '~async/main-product/sub-menu/sub-menu-coming-soon'
import SubMenuDetails from '~async/main-product/sub-menu/sub-menu-details'
import SubMenuDelivery from '~async/main-product/sub-menu/sub-menu-delivery'
import SubMenuHelp from '~async/main-product/sub-menu/sub-menu-help'
import SubMenuPreorder from '~async/main-product/sub-menu/sub-menu-preorder'
import SubMenuGiftWrapping from '~async/main-product/sub-menu/sub-menu-gift-wrapping'
import SubMenuSustainability from '~async/main-product/sub-menu/sub-menu-sustainability'

import { mapActions, mapGetters } from 'vuex'

import TransitionHeight from '~async/transition-height/transition-height'

export default {
  name: 'AccordionBlock',

  components: {
    IconArrowDown,
    IconArrowRight,
    IconArrowUp,
    IconPlus,
    IconMinus,
    SubMenuGarderob,
    SubMenuComingSoon,
    SubMenuDetails,
    SubMenuDelivery,
    SubMenuHelp,
    SubMenuPreorder,
    SubMenuGiftWrapping,
    SubMenuSustainability,
    TransitionHeight,
  },

  props: {
    accordionItems: {
      type: Array,
      default: () => [],
    },

    subMenuContent: {
      type: Object,
      default: () => ({}),
    },

    activeItem: {
      type: Object,
      default: () => ({}),
    },

    spacing: {
      type: Boolean,
      default: true,
    },

    icon: {
      type: String,
      default: 'chevron',
    },

    openByDefault: {
      type: String,
      default: '',
    },

    isMobile: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      activePanel: null,
    }
  },
  computed: {
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Check if any accordion item is currently active.
     * @returns {Boolean}
     */
    isOpen() {
      return Boolean(this.activePanel)
    },
  },

  watch: {

    /**
     * Watch form active item changes
     */
    activeItem() {
      if (this.activeItem && this.activeItem?.link !== this.activePanel) {
        this.handlePanelClick(null, this.activeItem.link)
      }
    },
  },

  mounted() {
    if (this.openByDefault) {
      this.openDefault(this.openByDefault)
    }
  },

  methods: {

    ...mapActions({
      openOverlay: 'overlays/open',
      setSizeGuideActive: 'product/setSizeGuideActive',
    }),

    openDefault(defaultItemLink) {
      const defaultItem = this.accordionItems.find(item => item.link === defaultItemLink)
      if (defaultItem) {
        this.handlePanelClick(null, defaultItem.link)
      }
    },

    /**
     * Returns the component name of the presently selected sub-menu component.
     * @param {String} subMenuItem - Name of the sub menu item.
     * @returns {String}
     */
    subMenuComponent(subMenuItem) {
      if (!subMenuItem) {
        return false
      }

      return `sub-menu-${subMenuItem}`
    },

    handleOverlayClick(event) {
      if (!this.isMobile) {
        this.setSizeGuideActive(true)

        if (event.target.closest('[data-gtm-event="pdp-secondary-links"]')) {
          this.handleGtm('size-guide')
        }
      }

      this.openOverlay({
        component: 'size-guide',
        ignoreDismissed: true,
        namespace: 'sizeGuide',
      })
    },

    /**
     * Handle the click event on an accordion panel.
     * @param {String} link - Active panel link.
     */
    handlePanelClick(event, link) {
      if (link === this.activePanel) {
        this.activePanel = null
        return
      }

      this.activePanel = link

      if (!event) {
        return
      }

      if (event.target.closest('[data-gtm-event="pdp-secondary-links"]')) {
        this.handleGtm(this.activePanel)
      }
    },

    handleGtm(activePanel) {
      const dataLayer = window.dataLayer || []
      const target = activePanel.replace('-', '')
      const targetCapitalised = target.charAt(0).toUpperCase() + target.slice(1)
      const event = `pdp_${target}_click`
      const eventDetailsObj = `pdp${targetCapitalised}Click`

      dataLayer.push({
        event,
        [eventDetailsObj]: {
          link_type: 'mobile_accordion',
          url: document.location.pathname,
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import './accordion-block';
</style>
